import { useParams, Link } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, Clock3, Sparkles } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { BookButton, WhatsAppButton } from "../components/CTAButtons";
import { getService, SERVICES, SIGNATURE_PACKAGE, ZORBA } from "../data/content";
import { breadcrumbSchema, faqSchema, multiGraph, serviceSchema } from "../components/Schema";
import NotFound from "./NotFound";

export default function ServiceDetail({ onBook }) {
  const { slug } = useParams();
  const service = getService(slug);
  if (!service) return <NotFound />;

  const crumbs = [
    { label: "Home", href: "/" },
    { label: "Services", href: "/spa-services-bhubaneswar/" },
    { label: service.name },
  ];

  const allServices = [SIGNATURE_PACKAGE, ...SERVICES];
  const related = (service.related || [])
    .map((s) => allServices.find((x) => x.slug === s))
    .filter(Boolean);

  return (
    <>
      <Seo
        title={`${service.name} in Bhubaneswar`}
        description={service.intro.slice(0, 158)}
        path={`/services/${service.slug}/`}
        image={service.image}
        jsonLd={multiGraph(
          serviceSchema(service),
          faqSchema(service.faqs || []),
          breadcrumbSchema(crumbs)
        )}
      />
      <section
        className="detail-hero"
        style={{ backgroundImage: `linear-gradient(90deg, rgba(27,67,50,.82), rgba(27,67,50,.45)), url(${service.image})` }}
      >
        <div className="detail-hero-inner">
          <Breadcrumbs items={crumbs} />
          <p className="hero-label"><span className="pulse-dot" /> {service.kicker}</p>
          <h1>{service.name}<br /><em>in Bhubaneswar.</em></h1>
          <p className="hero-copy">{service.short}</p>
          <div className="hero-actions">
            <BookButton onBook={onBook} service={`${service.name} enquiry`} label={`Enquire about ${service.name}`} testid="service-book-cta" />
            <WhatsAppButton
              label="WhatsApp our partner"
              message={`Hello, I'd like to enquire about ${service.name} at Zorba International Spa, Bhubaneswar.`}
              className="btn btn-outline-light"
              source={`service_${service.slug}`}
              testid="service-whatsapp-cta"
            />
          </div>
          <div className="hero-chips">
            {(service.priceOptions || []).map((p) => (
              <span key={p.duration} className="chip">
                <Clock3 size={13} /> {p.duration} · {p.display}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="service-detail section-pad">
        <div className="service-detail-grid">
          <article>
            <p className="eyebrow">What it is</p>
            <h2>About {service.name.toLowerCase()}</h2>
            <p className="large-copy">{service.intro}</p>

            {service.inclusions && service.inclusions.length > 0 && (
              <>
                <h3>What this signature journey includes</h3>
                <ul className="check-list">
                  {service.inclusions.map((x) => <li key={x}><Check size={16} />{x}</li>)}
                </ul>
              </>
            )}

            {service.styleOptions && service.styleOptions.length > 0 && (
              <>
                <h3>Style options for each partner</h3>
                <div className="pill-row">
                  {service.styleOptions.map((s) => <span key={s} className="pill">{s}</span>)}
                </div>
              </>
            )}

            {service.suitsWho && (
              <>
                <h3>Who it tends to suit</h3>
                <ul className="check-list">
                  {service.suitsWho.map((x) => <li key={x}><Check size={16} />{x}</li>)}
                </ul>
              </>
            )}

            {service.experience && (
              <>
                <h3>What the experience includes</h3>
                <ul className="check-list">
                  {service.experience.map((x) => <li key={x}><Check size={16} />{x}</li>)}
                </ul>
              </>
            )}

            {service.before && service.aftercare && (
              <div className="two-col">
                <div>
                  <h3>Before your appointment</h3>
                  <ul className="check-list">
                    {service.before.map((x) => <li key={x}><Check size={16} />{x}</li>)}
                  </ul>
                </div>
                <div>
                  <h3>Aftercare</h3>
                  <ul className="check-list">
                    {service.aftercare.map((x) => <li key={x}><Check size={16} />{x}</li>)}
                  </ul>
                </div>
              </div>
            )}

            <h3>Pricing at {ZORBA.name}</h3>
            <table className="price-table" data-testid="service-price-table">
              <thead>
                <tr>
                  <th>Duration</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {service.priceOptions.map((p) => (
                  <tr key={p.duration}>
                    <td>{p.duration}</td>
                    <td>{p.display}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted-small">Prices verified from the official Zorba International Spa menu.</p>

            {service.faqs && (
              <>
                <h3>Frequently asked questions</h3>
                <div className="faq-list simple">
                  {service.faqs.map((faq, i) => (
                    <details key={faq.q} open={i === 0}>
                      <summary data-testid={`service-faq-${i}`}>{faq.q} <ChevronDown size={18} /></summary>
                      <p>{faq.a}</p>
                    </details>
                  ))}
                </div>
              </>
            )}
          </article>

          <aside className="info-box sticky">
            <Sparkles size={20} />
            <strong>Enquire about {service.name}</strong>
            <span>Share your preferred time and the number of guests.</span>
            <small>No real-time availability is claimed. The spa will confirm directly.</small>
            <BookButton onBook={onBook} service={`${service.name} enquiry`} label="Start enquiry" testid="service-aside-book" />
            <WhatsAppButton
              label="WhatsApp"
              className="btn btn-secondary"
              message={`Hello, I'd like to enquire about ${service.name} at Zorba International Spa, Bhubaneswar.`}
              source={`service_aside_${service.slug}`}
              testid="service-aside-whatsapp"
            />
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="section-pad related-services">
          <h2>Related services</h2>
          <div className="service-grid small">
            {related.map((r, i) => (
              <Link to={`/services/${r.slug}/`} className="service-card" key={r.slug} data-testid={`related-service-${i}`}>
                <div className="service-photo" style={{ backgroundImage: `url(${r.image})` }} />
                <div className="service-card-copy">
                  <span className="card-kicker">{r.kicker}</span>
                  <h3>{r.name}</h3>
                  <p>{r.short}</p>
                  <ArrowRight size={18} />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
